import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Users, Lightbulb, Target, Award, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import AnimatedText from '../components/AnimatedText';
import FloatingCard from '../components/FloatingCard';
import ShimmerButton from '../components/ShimmerButton';
import SunGraphic from '../components/SunGraphic';

const Home = () => {
  const services = [
    {
      icon: <Target className="w-8 h-8 text-amber-500" />,
      title: "Stratégie de Marque",
      description: "Développez des identités de marque convaincantes qui résonnent avec votre audience cible et stimulent la croissance de votre entreprise."
    },
    {
      icon: <Lightbulb className="w-8 h-8 text-amber-500" />,
      title: "Création Visuelle",
      description: "Transformez votre vision en expériences visuelles époustouflantes qui captivent et inspirent vos clients."
    },
    {
      icon: <Users className="w-8 h-8 text-amber-500" />,
      title: "Marketing Digital",
      description: "Connectez-vous avec votre audience grâce à des campagnes digitales stratégiques qui offrent des résultats mesurables."
    }
  ];

  const stats = [
    { number: "150+", label: "Projets Réalisés" },
    { number: "50+", label: "Clients Satisfaits" },
    { number: "1+", label: "Année d'Expérience" },
    { number: "24/7", label: "Support Disponible" }
  ];

  const features = [
    "Développement stratégique de marque",
    "Design visuel créatif",
    "Expertise en marketing digital",
    "Solutions web responsives",
    "Services de création de contenu",
    "Gestion des réseaux sociaux"
  ];

  return (
    <div className="min-h-screen relative bg-gray-900">
      {/* Section Hero */}
      <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 overflow-hidden">
        <SunGraphic />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(251,191,36,0.1),transparent_70%)]"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32 z-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <AnimatedText 
                  className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight"
                  isH1={true}
                  delay={200}
                >
                  Illuminez Votre Marque avec
                  <span className="title-gradient"> Soleil d&apos;Azur</span>
                </AnimatedText>
                <AnimatedText 
                  className="text-xl text-gray-300 leading-relaxed font-inter"
                  delay={400}
                >
                  Nous créons des solutions de communication exceptionnelles qui connectent les marques avec leur audience grâce à un design innovant et une réflexion stratégique.
                </AnimatedText>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <AnimatedText delay={600}>
                  <ShimmerButton asChild size="lg" className="bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white px-8 py-6 text-lg rounded-full">
                    <Link to="/contact">
                      Commencer Aujourd&apos;hui
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </Link>
                  </ShimmerButton>
                </AnimatedText>
                <AnimatedText delay={800}>
                  <ShimmerButton asChild variant="outline" size="lg" className="border-amber-500 text-amber-400 hover:bg-amber-500/10 px-8 py-6 text-lg rounded-full">
                    <Link to="/plans">Voir Nos Formules</Link>
                  </ShimmerButton>
                </AnimatedText>
              </div>
            </div>
            <div className="lg:pl-8">
              <AnimatedText delay={1000}>
                <FloatingCard className="bg-gray-800 border border-gray-700" delay={0.5}>
                  <div className="p-8">
                    <div className="grid grid-cols-2 gap-6">
                      {stats.map((stat, index) => (
                        <div key={index} className="text-center">
                          <AnimatedText 
                            className="text-3xl font-inter font-bold text-white mb-2"
                            delay={1200 + index * 100}
                            isTitle={true}
                          >
                            {stat.number}
                          </AnimatedText>
                          <AnimatedText 
                            className="text-sm text-gray-400 font-inter"
                            delay={1300 + index * 100}
                          >
                            {stat.label}
                          </AnimatedText>
                        </div>
                      ))}
                    </div>
                  </div>
                </FloatingCard>
              </AnimatedText>
            </div>
          </div>
        </div>
      </section>

      {/* Section Services Principaux */}
      <section className="py-20 bg-gray-900 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <AnimatedText 
              className="text-3xl sm:text-4xl font-inter font-bold text-white mb-4"
              isTitle={true}
            >
              Nos Services Principaux
            </AnimatedText>
            <AnimatedText 
              className="text-xl text-gray-400 max-w-3xl mx-auto font-inter"
              delay={200}
            >
              Nous offrons des solutions de communication complètes conçues pour élever votre marque et connecter avec votre audience.
            </AnimatedText>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <AnimatedText key={index} delay={300 + index * 100}>
                <FloatingCard className="bg-gray-800 border border-gray-700 hover:border-amber-500/50 transition-all duration-300" delay={index * 0.2}>
                  <CardContent className="p-8">
                    <div className="mb-6">{service.icon}</div>
                    <AnimatedText 
                      className="text-xl font-inter font-semibold text-white mb-4"
                      isTitle={true}
                      delay={400 + index * 100}
                    >
                      {service.title}
                    </AnimatedText>
                    <AnimatedText 
                      className="text-gray-400 leading-relaxed font-inter"
                      delay={500 + index * 100}
                    >
                      {service.description}
                    </AnimatedText>
                  </CardContent>
                </FloatingCard>
              </AnimatedText>
            ))}
          </div>
        </div>
      </section>

      {/* Section Pourquoi Nous Choisir */}
      <section className="py-20 bg-gray-800 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <AnimatedText 
                  className="text-3xl sm:text-4xl font-inter font-bold text-white"
                  isTitle={true}
                >
                  Pourquoi Choisir Soleil d&apos;Azur ?
                </AnimatedText>
                <AnimatedText 
                  className="text-xl text-gray-400 font-inter"
                  delay={200}
                >
                  Notre expertise et dévouement garantissent que votre marque brille dans le marché concurrentiel.
                </AnimatedText>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {features.map((feature, index) => (
                  <AnimatedText key={index} delay={300 + index * 50}>
                    <div className="flex items-center space-x-3">
                      <CheckCircle className="w-5 h-5 text-amber-500 flex-shrink-0" />
                      <span className="text-gray-300 font-inter">{feature}</span>
                    </div>
                  </AnimatedText>
                ))}
              </div>
              <AnimatedText delay={800}>
                <ShimmerButton asChild size="lg" className="bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white rounded-full">
                  <Link to="/about">En Savoir Plus</Link>
                </ShimmerButton>
              </AnimatedText>
            </div>
            <div className="lg:pl-8">
              <AnimatedText delay={600}>
                <FloatingCard className="bg-gray-900 border border-gray-700" delay={0.8}>
                  <div className="p-8">
                    <div className="flex items-center space-x-4 mb-6">
                      <Award className="w-8 h-8 text-amber-500" />
                      <AnimatedText 
                        className="text-xl font-inter font-semibold text-white"
                        isTitle={true}
                        delay={700}
                      >
                        Agence Primée
                      </AnimatedText>
                    </div>
                    <AnimatedText 
                      className="text-gray-400 mb-6 font-inter"
                      delay={800}
                    >
                      Reconnue pour l&apos;excellence en design créatif et solutions de communication stratégiques.
                    </AnimatedText>
                    <div className="space-y-4">
                      <AnimatedText delay={900}>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-400 font-inter">Satisfaction Client</span>
                          <span className="text-sm font-semibold text-amber-500 font-inter">98%</span>
                        </div>
                      </AnimatedText>
                      <AnimatedText delay={1000}>
                        <div className="w-full bg-gray-700 rounded-full h-2">
                          <div className="bg-gradient-to-r from-amber-400 to-orange-500 h-2 rounded-full" style={{ width: '98%' }}></div>
                        </div>
                      </AnimatedText>
                    </div>
                  </div>
                </FloatingCard>
              </AnimatedText>
            </div>
          </div>
        </div>
      </section>

      {/* Section Appel à l'Action */}
      <section className="py-20 bg-gradient-to-r from-amber-600 to-orange-600 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-3xl mx-auto space-y-8">
            <AnimatedText 
              className="text-3xl sm:text-4xl font-inter font-bold text-white"
              isTitle={true}
            >
              Prêt à Illuminer Votre Marque ?
            </AnimatedText>
            <AnimatedText 
              className="text-xl text-amber-100 font-inter"
              delay={200}
            >
              Travaillons ensemble pour créer quelque chose d&apos;extraordinaire. Contactez-nous dès aujourd&apos;hui et découvrez comment nous pouvons faire briller votre marque.
            </AnimatedText>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <AnimatedText delay={400}>
                <ShimmerButton asChild size="lg" variant="secondary" className="bg-white text-amber-600 hover:bg-gray-100 px-8 py-6 text-lg rounded-full">
                  <Link to="/contact">Démarrer Votre Projet</Link>
                </ShimmerButton>
              </AnimatedText>
              <AnimatedText delay={600}>
                <ShimmerButton asChild size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-amber-600 px-8 py-6 text-lg rounded-full">
                  <Link to="/resources">Voir les Ressources</Link>
                </ShimmerButton>
              </AnimatedText>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
